// web/src/components/Store.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StoreProductPanel from "./StoreProductPanel";
import PageLayout from "./PageLayout";
import LimitReachedModal from "./LimitReachedModal";
import { Account } from "./TopMenuBar";
import { useViewport } from "../hooks/useViewport";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

interface Product {
  id: string;
  name: string;
  description: string;
  base_entries: number;
  usd_price: number;
  has_bonus: boolean;
  bonus_text?: string;
}

interface StoreProps {
  account: Account;
  onAccountRefresh?: () => void;
  onLogout?: () => void;
}

// Button positions extracted from Store_Buttons.png (1920x1080)
// Black pixels on transparent background - exact bounding boxes of non-transparent pixels
// These coordinates are the exact pixel positions where buttons should be clickable
const BUTTON_POSITIONS = [
  // Top row - left to right
  { 
    id: "steel_pan", 
    x: 290, 
    y: 180, 
    width: 100, 
    height: 100 
  }, // Top left - Steel Pan Kit
  { 
    id: "assayers_pack", 
    x: 600, 
    y: 180, 
    width: 100, 
    height: 100 
  }, // Top right - Assayer's Pack (shuffled again)
  // Middle row - left to right
  { 
    id: "expedition_crate", 
    x: 290, 
    y: 480, 
    width: 100, 
    height: 100 
  }, // Middle left - Expedition Crate
  { 
    id: "motherlode_vault", 
    x: 600, 
    y: 480, 
    width: 100, 
    height: 100 
  }, // Middle right - Motherlode Vault
  // Bottom row - left to right
  { 
    id: "claim_jumper_rig", 
    x: 290, 
    y: 780, 
    width: 100, 
    height: 100 
  }, // Bottom left - Claim Jumper Rig (shuffled again)
  { 
    id: "tin_pan", 
    x: 600, 
    y: 780, 
    width: 100, 
    height: 100 
  }, // Bottom right - Tin Pan Starter (shuffled again) (moved from top right)
];

// Compute the exact cover transform for object-fit: cover
function computeCoverTransform(
  containerW: number,
  containerH: number,
  naturalW = 1920,
  naturalH = 1080
) {
  // object-fit: cover => scale is MAX of the two ratios
  const scale = Math.max(containerW / naturalW, containerH / naturalH);
  const renderedW = naturalW * scale;
  const renderedH = naturalH * scale;

  // object-position: center => leftover space is split equally
  const offsetX = (containerW - renderedW) / 2;
  const offsetY = (containerH - renderedH) / 2;

  return { scale, offsetX, offsetY };
}

// Helper to get center point of a bounding box
function center(b: {x: number; y: number; width: number; height: number}) {
  return { cx: b.x + b.width / 2, cy: b.y + b.height / 2 };
}

// Map detected buttons to expected positions by closest proximity
function mapDetectedToExpected(
  detected: Array<{x: number; y: number; width: number; height: number}>
) {
  // Optional: ignore tiny garbage blobs
  const det = detected.filter(b => b.width * b.height > 2000);

  const used = new Set<number>();

  return BUTTON_POSITIONS.map((expected) => {
    const e = center(expected);

    let bestIdx = -1;
    let bestDist = Infinity;

    for (let i = 0; i < det.length; i++) {
      if (used.has(i)) continue;
      const d = center(det[i]);
      const dist = (d.cx - e.cx) ** 2 + (d.cy - e.cy) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    // Fallback: if detection fails, use hardcoded expected
    if (bestIdx === -1) return expected;

    used.add(bestIdx);
    return { id: expected.id, ...det[bestIdx] };
  });
}

export default function Store({ account, onAccountRefresh, onLogout }: StoreProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [detectedPositions, setDetectedPositions] = useState<typeof BUTTON_POSITIONS | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'cancelled' | null, text: string }>({ type: null, text: '' });
  const [showCompleteRegistrationModal, setShowCompleteRegistrationModal] = useState(false);
  
  // Refs and state for cover transform calculation
  const storeContainerRef = useRef<HTMLDivElement | null>(null);
  const [coverTx, setCoverTx] = useState<{ scale: number; offsetX: number; offsetY: number } | null>(null);

  // Compute cover transform when container size changes (only for mobile landscape)
  useEffect(() => {
    // Only compute cover transform for mobile landscape where we need to handle cropping
    if (!isMobileLandscape) {
      setCoverTx(null);
      return;
    }

    const el = storeContainerRef.current;
    if (!el) {
      setCoverTx(null);
      return;
    }

    const update = () => {
      const r = el.getBoundingClientRect();
      setCoverTx(computeCoverTransform(r.width, r.height, 1920, 1080));
    };

    // Small delay to ensure container is rendered
    const timeoutId = setTimeout(update, 0);

    const ro = new ResizeObserver(update);
    ro.observe(el);

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [isMobileLandscape]);

  useEffect(() => {
    fetchProducts();
    detectButtonPositions();
  }, []);

  // Check if we should show complete registration modal on mount (from navigation state)
  useEffect(() => {
    if (location.state?.showCompleteRegistrationModal && account && !account.email_verified) {
      setShowCompleteRegistrationModal(true);
      // Clear the state so it doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, account]);

  // Separate effect for payment redirect handling - only runs once on mount
  useEffect(() => {
    
    // Check for payment redirect parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    if (payment === 'success') {
      setPaymentMessage({ type: 'success', text: 'Payment successful! Your entries have been added to your account.' });
      // Refresh account data
      if (onAccountRefresh) {
        onAccountRefresh();
      }
      urlParams.delete('payment');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
      // Clear message after 5 seconds
      setTimeout(() => setPaymentMessage({ type: null, text: '' }), 5000);
    } else if (payment === 'cancelled') {
      setPaymentMessage({ type: 'cancelled', text: 'Payment was cancelled. No charges were made.' });
      // Clear the URL parameter
      urlParams.delete('payment');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
      // Clear message after 5 seconds
      setTimeout(() => setPaymentMessage({ type: null, text: '' }), 5000);
    }
  }, [onAccountRefresh]); // Only depend on onAccountRefresh

  async function detectButtonPositions() {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1920;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, 1920, 1080);
      const data = imageData.data;
      
      // Find all non-transparent pixels
      const nonTransparentPixels: Array<{x: number, y: number}> = [];
      for (let y = 0; y < 1080; y++) {
        for (let x = 0; x < 1920; x++) {
          const index = (y * 1920 + x) * 4;
          const alpha = data[index + 3];
          if (alpha > 0) {
            nonTransparentPixels.push({ x, y });
          }
        }
      }
      
      // Group pixels into bounding boxes (buttons)
      const buttons: Array<{x: number, y: number, width: number, height: number}> = [];
      const processed = new Set<string>();
      
      for (const pixel of nonTransparentPixels) {
        const key = `${pixel.x},${pixel.y}`;
        if (processed.has(key)) continue;
        
        // Flood fill to find connected region
        const region: Array<{x: number, y: number}> = [];
        const stack = [pixel];
        
        while (stack.length > 0) {
          const p = stack.pop()!;
          const k = `${p.x},${p.y}`;
          if (processed.has(k)) continue;
          
          const idx = (p.y * 1920 + p.x) * 4;
          if (data[idx + 3] > 0) {
            processed.add(k);
            region.push(p);
            
            // Check neighbors
            if (p.x > 0) stack.push({ x: p.x - 1, y: p.y });
            if (p.x < 1919) stack.push({ x: p.x + 1, y: p.y });
            if (p.y > 0) stack.push({ x: p.x, y: p.y - 1 });
            if (p.y < 1079) stack.push({ x: p.x, y: p.y + 1 });
          }
        }
        
        if (region.length > 50) { // Minimum button size
          const minX = Math.min(...region.map(p => p.x));
          const maxX = Math.max(...region.map(p => p.x));
          const minY = Math.min(...region.map(p => p.y));
          const maxY = Math.max(...region.map(p => p.y));
          
          buttons.push({
            x: minX,
            y: minY,
            width: maxX - minX + 1,
            height: maxY - minY + 1
          });
        }
      }
      
      // Sort by position (top to bottom, left to right)
      buttons.sort((a, b) => {
        if (Math.abs(a.y - b.y) < 100) return a.x - b.x; // Same row, sort by x
        return a.y - b.y; // Different rows, sort by y
      });
      
      console.log('Detected regions:', buttons.length);
      console.log('Detected button positions from Store_Buttons.png:', buttons);
      
      // Map detected buttons to product IDs by closest proximity to expected positions
      setDetectedPositions(mapDetectedToExpected(buttons));
    };
    img.src = './Store_Buttons.png';
  }

  async function fetchProducts() {
    try {
      const res = await fetch(`${API_BASE}/store/products`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch products");
      const data = await res.json();
      setProducts(data.products);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  }

  const [clickedButtonPosition, setClickedButtonPosition] = useState<{ x: number; y: number } | null>(null);

  function handleProductClick(productId: string, event: React.MouseEvent<HTMLButtonElement>) {
    const product = products.find((p) => p.id === productId);
    if (product) {
      const rect = event.currentTarget.getBoundingClientRect();
      const containerRect = (event.currentTarget.closest('.relative') as HTMLElement)?.getBoundingClientRect();
      if (containerRect) {
        // Position relative to the store container
        const x = rect.left - containerRect.left + rect.width;
        const y = rect.top - containerRect.top;
        setClickedButtonPosition({ x, y });
        setSelectedProduct(product);
      } else {
        setSelectedProduct(product);
        setClickedButtonPosition(null);
      }
    }
  }

  function handleClosePanel() {
    setSelectedProduct(null);
    setClickedButtonPosition(null);
  }

  async function handlePurchaseSuccess() {
    // Refresh account data - this will update the menu bar
    setSelectedProduct(null);
    // Refresh account data without page reload
    if (onAccountRefresh) {
      await onAccountRefresh();
    } else {
      // Fallback: refresh account manually
      try {
        const res = await fetch(`${API_BASE}/account`, {
          credentials: "include",
        });
        if (res.ok) {
          const accountData = await res.json();
          // Update account in parent component
          window.dispatchEvent(new CustomEvent('accountUpdated', { detail: accountData }));
        }
      } catch (err) {
        console.error('Failed to refresh account:', err);
      }
    }
  }

  // Create product map for quick lookup
  const productMap = new Map(products.map((p) => [p.id, p]));

  return (
    <PageLayout account={account} onLogout={onLogout}>
      
      {/* Payment Status Message */}
      {paymentMessage.type && (
        <div
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-xl shadow-lg max-w-md"
          style={{
            background: paymentMessage.type === 'success' ? '#d4edda' : '#f8d7da',
            border: `2px solid ${paymentMessage.type === 'success' ? '#28a745' : '#dc3545'}`,
            color: paymentMessage.type === 'success' ? '#155724' : '#721c24',
          }}
        >
          <div className="flex items-center justify-between">
            <span className="font-semibold">{paymentMessage.text}</span>
            <button
              onClick={() => setPaymentMessage({ type: null, text: '' })}
              className="ml-4 text-lg font-bold opacity-70 hover:opacity-100"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Combined container for background and button overlay - they transform together */}
      {isMobileLandscape ? (
        <div 
          ref={storeContainerRef}
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ 
            margin: 0, 
            padding: 0, 
            zIndex: 0,
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: 'fixed'
          }}
        >
          {/* Background Image */}
          <img
            src="./Store.png"
            alt="General Store"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              objectPosition: 'center',
              opacity: selectedProduct ? 0.5 : 1,
              display: 'block'
            }}
            draggable={false}
          />
          
          {/* Button Overlay - same container, same transform */}
          <div 
            className="absolute inset-0 w-full h-full"
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onWheel={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onScroll={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              margin: 0,
              padding: 0,
              zIndex: 40,
              background: 'transparent',
              pointerEvents: 'none',
              overflow: 'hidden',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              position: 'absolute'
            }}
          >
            {/* Dim overlay when panel is open */}
            {selectedProduct && (
              <div 
                className="absolute inset-0 bg-black transition-opacity duration-300 z-20"
                style={{ opacity: 0.3, pointerEvents: 'none' }}
              />
            )}

            {/* Clickable Button Regions */}
            {coverTx && (detectedPositions || BUTTON_POSITIONS).map((pos) => {
              const product = productMap.get(pos.id);
              if (!product) return null;

              const { scale, offsetX, offsetY } = coverTx;

              return (
                <button
                  key={pos.id}
                  onClick={(e) => handleProductClick(pos.id, e)}
                  onMouseEnter={() => setHoveredButton(pos.id)}
                  onMouseLeave={() => setHoveredButton(null)}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onDrag={(e) => e.preventDefault()}
                  className="absolute cursor-pointer transition-all duration-200"
                  style={{
                    pointerEvents: 'auto',
                    zIndex: 30,
                    left: `${offsetX + pos.x * scale}px`,
                    top: `${offsetY + pos.y * scale}px`,
                    width: `${pos.width * scale}px`,
                    height: `${pos.height * scale}px`,
                    background: hoveredButton === pos.id ? "rgba(255, 255, 255, 0.2)" : "transparent",
                    border: hoveredButton === pos.id ? "3px solid rgba(255, 230, 0, 0.8)" : "2px solid rgba(255, 230, 0, 0.25)",
                    borderRadius: "8px",
                    boxShadow:
                      hoveredButton === pos.id
                        ? "0 0 20px rgba(255, 230, 0, 0.6), inset 0 0 20px rgba(255, 230, 0, 0.3)"
                        : "0 0 12px rgba(255, 230, 0, 0.3), inset 0 0 12px rgba(255, 230, 0, 0.15)",
                    touchAction: 'none',
                    userSelect: 'none'
                  }}
                  aria-label={`View ${product.name}`}
                />
              );
            })}

            {/* Enter Mine Button - Bottom Right */}
            {coverTx && (
              <button
                onClick={() => navigate("/play")}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                onDrag={(e) => e.preventDefault()}
                className="absolute z-30 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap bg-[#fffaeb] text-[#3e2615] border-2 border-[#8b6542] shadow-[0_3px_0_#8b6542,0_5px_10px_rgba(0,0,0,0.25)]"
                style={{
                  left: `${coverTx.offsetX + 1174 * coverTx.scale}px`,
                  bottom: `${coverTx.offsetY + (1080 - 1000) * coverTx.scale + 30}px`,
                  width: `${(1464 - 1174) * coverTx.scale}px`,
                minWidth: 'fit-content',
                padding: "clamp(6px, 1.5vw, 10px) clamp(12px, 2vw, 18px)",
                fontSize: "clamp(12px, 2vw, 16px)",
                opacity: selectedProduct ? 0.5 : 1,
                transition: "opacity 0.3s, transform 0.2s",
                pointerEvents: selectedProduct ? "none" : "auto",
                touchAction: 'none',
                userSelect: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "translateY(1px)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
            >
                Enter Mine
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="px-2 md:px-6 pb-2 md:pb-4">
          <div 
            ref={storeContainerRef}
            className="relative w-full rounded-2xl border-4 overflow-hidden shadow-xl md:max-h-[95vh]"
            style={{
              borderColor: '#8b6542',
              background: '#fffaeb',
              aspectRatio: '16/9',
              maxWidth: '100%'
            }}
          >
            {/* Background Image - only for non-mobile landscape */}
            {!isMobileLandscape && (
          <img
            src="./Store.png"
            alt="General Store"
            className="absolute inset-0 w-full h-full transition-opacity duration-300"
            style={{ 
              objectFit: "cover", 
              objectPosition: "center",
              opacity: selectedProduct ? 0.5 : 1,
              width: '100%',
              height: '100%',
              display: 'block'
            }}
            draggable={false}
          />
        )}

        {/* Dim overlay when panel is open */}
        {selectedProduct && (
          <div 
            className="absolute inset-0 bg-black transition-opacity duration-300 z-20"
            style={{ opacity: 0.3, pointerEvents: 'none' }}
          />
        )}
            {/* Clickable Button Regions */}
            {(detectedPositions || BUTTON_POSITIONS).map((pos) => {
              const product = productMap.get(pos.id);
              if (!product) return null;

              // Desktop: use percentage-based positioning (no cropping with matching aspect ratio)
              // Mobile landscape: use cover transform (handles cropping)
              const useCoverTransform = isMobileLandscape && coverTx;
              
              return (
                <button
                  key={pos.id}
                  onClick={(e) => handleProductClick(pos.id, e)}
                  onMouseEnter={() => setHoveredButton(pos.id)}
                  onMouseLeave={() => setHoveredButton(null)}
                  className="absolute cursor-pointer transition-all duration-200"
                  style={{
                    left: useCoverTransform 
                      ? `${coverTx!.offsetX + pos.x * coverTx!.scale}px`
                      : `${(pos.x / 1920) * 100}%`,
                    top: useCoverTransform
                      ? `${coverTx!.offsetY + pos.y * coverTx!.scale}px`
                      : `${(pos.y / 1080) * 100}%`,
                    width: useCoverTransform
                      ? `${pos.width * coverTx!.scale}px`
                      : `${(pos.width / 1920) * 100}%`,
                    height: useCoverTransform
                      ? `${pos.height * coverTx!.scale}px`
                      : `${(pos.height / 1080) * 100}%`,
                    background: hoveredButton === pos.id ? "rgba(255, 255, 255, 0.2)" : "transparent",
                    border: hoveredButton === pos.id ? "3px solid rgba(255, 230, 0, 0.8)" : "2px solid rgba(255, 230, 0, 0.25)",
                    borderRadius: "8px",
                    boxShadow:
                      hoveredButton === pos.id
                        ? "0 0 20px rgba(255, 230, 0, 0.6), inset 0 0 20px rgba(255, 230, 0, 0.3)"
                        : "0 0 12px rgba(255, 230, 0, 0.3), inset 0 0 12px rgba(255, 230, 0, 0.15)",
                  }}
                  aria-label={`View ${product.name}`}
                />
              );
            })}

            {/* Enter Mine Button - Bottom Right (Desktop) */}
            <button
              onClick={() => navigate("/play")}
              className="absolute z-30 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap bg-[#fffaeb] text-[#3e2615] border-2 border-[#8b6542] shadow-[0_3px_0_#8b6542,0_5px_10px_rgba(0,0,0,0.25)]"
              style={{
                left: `${((1174 + (1464 - 1174) / 2) / 1920) * 100}%`,
                bottom: `${((1080 - 1000) / 1080) * 100}%`,
                width: `${((1464 - 1174) / 1920) * 100}%`,
                minWidth: 'fit-content',
                padding: "10px 18px",
                fontSize: "16px",
                opacity: selectedProduct ? 0.5 : 1,
                transition: "opacity 0.3s, transform 0.2s",
                pointerEvents: selectedProduct ? "none" : "auto",
              }}
              onMouseEnter={(e) => {
                if (!selectedProduct) {
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedProduct) {
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
              onMouseDown={(e) => {
                if (!selectedProduct) {
                  e.currentTarget.style.transform = "translateY(1px)";
                }
              }}
              onMouseUp={(e) => {
                if (!selectedProduct) {
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
            >
              Enter Mine
            </button>
          </div>
        </div>
      )}

      {/* Product Detail Panel */}
      {selectedProduct && (
        <StoreProductPanel
          product={selectedProduct}
          onClose={handleClosePanel}
          onPurchaseSuccess={handlePurchaseSuccess}
          position={clickedButtonPosition}
          account={account}
          onShowCompleteRegistration={() => {
            setShowCompleteRegistrationModal(true);
          }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-30">
          <div className="text-white text-xl">Loading store...</div>
        </div>
      )}

      {/* Complete Registration Modal - shown when guest tries to purchase */}
      <LimitReachedModal
        isOpen={showCompleteRegistrationModal}
        onClose={() => {
          setShowCompleteRegistrationModal(false);
        }}
        onCompleteRegistration={async () => {
          setShowCompleteRegistrationModal(false);
          // Refresh account after completing registration
          if (onAccountRefresh) {
            await onAccountRefresh();
          }
        }}
        apiBase={API_BASE}
        hasEmail={account?.email ? !account.email.endsWith('@guest.local') : false}
        allowClose={true}
        title="Complete Registration"
      />
    </PageLayout>
  );
}
